'use client';
import { OrderLoyaltyReward } from '@eliadmin/core/src/components/loyalty/order-loyalty-reward';

import { useId } from 'react';

import type { ImageType } from '@eliadmin/core/src/api/api-types';
import type { CustomerOrder } from '@eliadmin/core/src/api/customers/customers-types';
import {
  CARD,
  OrderStatusBadge,
  OrderThumb,
  ReorderMessage,
  TEXT_LINK,
} from '@eliadmin/core/src/components/nt/account/orders/order-parts';
import {
  orderStage,
  stageFilter,
} from '@eliadmin/core/src/components/nt/account/orders/order-stage';
import {
  countLabels,
  useOrderTexts,
} from '@eliadmin/core/src/components/nt/account/orders/order-texts';
import {
  orderCounts,
  orderDate,
  orderNumber,
  orderTotal,
  trackingUrl,
  useOrderInfo,
  usePrice,
} from '@eliadmin/core/src/components/nt/account/orders/orders-data';
import { useOrderDocument } from '@eliadmin/core/src/components/nt/account/orders/use-order-document';
import { useReorder } from '@eliadmin/core/src/components/nt/account/orders/use-reorder';
import { REORDER_LABEL } from '@eliadmin/core/src/components/nt/account/orders/reorder-action';
import { orderDetailHref } from '@eliadmin/core/src/components/nt/account/shell/account-pages';
import Link from '@eliadmin/core/src/components/link';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { fillTemplate } from '@eliadmin/core/src/utils/nt/text';
import { cn } from '@eliadmin/core/src/utils/style-utils';

/** Five 56 px photos fit the 324 px phone card; a longer order shows four and „+N“. */
const MAX_THUMBS = 5;

/**
 * Figma „Thumbs“: the ordered products' photos, 8 px apart, „+N“ for the rest. Takes the lines of an
 * order or of a returned one (name and photo are all it reads).
 */
export function OrderThumbs({
  items,
}: {
  items: { id: number | string; name: string; image?: Pick<ImageType, 'url'> | null }[];
}) {
  const shown = items.length > MAX_THUMBS ? items.slice(0, MAX_THUMBS - 1) : items;
  const rest = items.length - shown.length;

  return (
    <ul className="flex gap-2">
      {shown.map(item => (
        <li key={item.id}>
          <OrderThumb image={item.image} alt={item.name} />
        </li>
      ))}
      {rest ? (
        <li className="nt-body-m-semibold flex size-14 items-center justify-center rounded-xl border border-[#E2E5DF] bg-[#F5F6F3] text-[#363D36]">
          +{rest}
        </li>
      ) : null}
    </ul>
  );
}

/**
 * Figma „Order · …“ (207:11671): number with the status pill and the date, the total with the
 * product count, the product photos and, under a divider, „Detail objednávky“, „Sledovať
 * zásielku“ (only when the carrier link exists – asked from `/orders/info` for orders in progress
 * only), „Faktúra (PDF)“ (only when a document exists) and „Kúpiť znova“. An order in progress
 * has the 2 px green frame. From a 38rem column the actions are one row; narrower the two buttons
 * share a row and the text links wrap under them.
 */
export function OrderCard({ order }: { order: CustomerOrder }) {
  const { t } = useTranslations();
  const { account, checkout } = useOrderTexts();
  const price = usePrice();
  const titleId = useId();
  const number = orderNumber(order);
  const stage = orderStage(order.status);
  const active = stageFilter(stage) === 'active';
  const info = useOrderInfo(number, active);
  const tracking = trackingUrl(info.data);
  const { document, download, loading } = useOrderDocument(order.documents);
  const reorder = useReorder(order);
  const counts = countLabels(checkout, orderCounts(order.items));
  const summary = checkout && counts ? fillTemplate(checkout.itemsSummary, counts) : '';

  return (
    <article
      aria-labelledby={titleId}
      className={cn(
        CARD,
        'flex flex-col gap-4 p-4 @[38rem]:px-6 @[38rem]:py-5',
        active && 'border-2 border-[#127A26]',
      )}
    >
      <div className="flex items-start gap-4">
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
            <h2 id={titleId} className="nt-body-l-bold text-[#0D110D]">
              {fillTemplate(account.order, { cislo: number })}
            </h2>
            <OrderStatusBadge status={order.status} stage={stage} />
          </div>
          <p className="nt-caption text-[#5D655D]">
            {account.createdOn
              ? fillTemplate(account.createdOn, { datum: orderDate(order.created_at) })
              : `${t('text18')} ${orderDate(order.created_at)}`}
          </p>
        </div>
        <p className="flex shrink-0 flex-col items-end text-right">
          <span className="nt-price-m text-[#0D110D]">{price(orderTotal(order))}</span>
          {summary ? <span className="nt-caption text-[#5D655D]">{summary}</span> : null}
        </p>
      </div>

      {order.items.length ? <OrderThumbs items={order.items} /> : null}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-3 border-t border-[#E2E5DF] pt-4 @[38rem]:flex-nowrap">
        <Button
          asChild
          variant="outline"
          size="md"
          className="flex-1 @[38rem]:flex-none"
          aria-describedby={titleId}
        >
          <Link href={orderDetailHref(number)} prefetch={false}>
            {t('text17')}
          </Link>
        </Button>
        {tracking || document ? (
          <div className="order-last flex basis-full flex-wrap items-center gap-x-4 gap-y-2 @[38rem]:order-none @[38rem]:basis-auto">
            {tracking ? (
              <a
                href={tracking}
                target="_blank"
                rel="noopener noreferrer"
                aria-describedby={titleId}
                className={TEXT_LINK}
              >
                {account.trackShipment}
              </a>
            ) : null}
            {document ? (
              <button
                type="button"
                onClick={download}
                disabled={loading}
                aria-busy={loading || undefined}
                aria-describedby={titleId}
                className={TEXT_LINK}
              >
                {account.invoice}
              </button>
            ) : null}
          </div>
        ) : null}
        {reorder.available ? (
          <Button
            variant="ghost"
            size="md"
            iconLeft="arrow-clockwise"
            onClick={reorder.reorder}
            isLoading={reorder.pending}
            disabled={reorder.disabled}
            aria-describedby={titleId}
            className="w-full whitespace-normal @[38rem]:ml-auto @[38rem]:w-auto"
          >
            {REORDER_LABEL}
          </Button>
        ) : null}
      </div>
      <ReorderMessage result={reorder.result} />

      <OrderLoyaltyReward reward={order.loyalty_reward} compact className="w-full" />
    </article>
  );
}
